import React from 'react';
import Icon from '../../../components/AppIcon';

const ValuesSection = () => {
  const values = [
    {
      icon: "Heart",
      title: "Thoughtfulness First",
      description: "Every feature we build prioritizes meaningful connection over convenience, ensuring that technology enhances rather than replaces human thoughtfulness.",
      color: "primary"
    },
    {
      icon: "Globe",
      title: "Inclusive Celebration",
      description: "We honor diverse traditions, cultures, and relationship types, creating a platform where everyone feels represented and respected.",
      color: "secondary"
    },
    {
      icon: "Shield",
      title: "Privacy & Trust",
      description: "Your personal relationships and preferences are sacred. We protect your data with enterprise-grade security and transparent practices.",
      color: "accent"
    },
    {
      icon: "Lightbulb",
      title: "Continuous Learning",
      description: "We constantly evolve our understanding of gift-giving, relationships, and cultural nuances to serve you better.",
      color: "warning"
    },
    {
      icon: "Users",
      title: "Community-Driven",
      description: "Our platform grows stronger through shared experiences, collective wisdom, and the generous spirit of our gift-giving community.",
      color: "success"
    },
    {
      icon: "Zap",
      title: "Effortless Experience",
      description: "Complex technology should feel simple. We hide the complexity behind intuitive interfaces that make gift-giving joyful, not stressful.",
      color: "info"
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      primary: "from-primary-400 to-primary-600 text-white",
      secondary: "from-secondary-400 to-secondary-600 text-white",
      accent: "from-accent-400 to-accent-600 text-white",
      warning: "from-warning-400 to-warning-600 text-white",
      success: "from-success-400 to-success-600 text-white",
      info: "from-blue-400 to-blue-600 text-white"
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl lg:text-5xl font-bold text-accent-800 mb-6">
            Our Core Values
          </h2>
          <p className="text-lg lg:text-xl text-accent-600 max-w-3xl mx-auto leading-relaxed">
            These principles guide every decision we make, from product development to community building, ensuring we stay true to our mission of meaningful connection.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div key={index} className="group hover-lift">
              <div className="bg-white rounded-2xl p-8 shadow-md border border-accent-100 h-full transition-all duration-300 group-hover:shadow-lg group-hover:border-primary-200">
                <div className={`w-16 h-16 bg-gradient-to-br ${getColorClasses(value.color)} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={value.icon} size={28} />
                </div>
                
                <h3 className="font-heading text-xl font-bold text-accent-800 mb-4">
                  {value.title}
                </h3>
                
                <p className="text-accent-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Values in Action */}
        <div className="mt-16 bg-gradient-warm rounded-2xl p-8 lg:p-12 border border-primary-100">
          <div className="text-center mb-8">
            <h3 className="font-heading text-2xl lg:text-3xl font-bold text-accent-800 mb-4">
              Values in Action
            </h3>
            <p className="text-lg text-accent-600 max-w-2xl mx-auto">
              See how our values translate into real impact for our community
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Award" size={32} className="text-white" />
              </div>
              <h4 className="font-heading text-xl font-bold text-accent-800 mb-2">
                Cultural Sensitivity
              </h4>
              <p className="text-accent-600">
                Supporting 50+ cultural celebrations and traditions worldwide
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-secondary-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Lock" size={32} className="text-white" />
              </div>
              <h4 className="font-heading text-xl font-bold text-accent-800 mb-2">
                Data Protection
              </h4>
              <p className="text-accent-600">
                Zero data breaches with SOC 2 Type II compliance
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={32} className="text-white" />
              </div>
              <h4 className="font-heading text-xl font-bold text-accent-800 mb-2">
                Community Impact
              </h4>
              <p className="text-accent-600">
                Over 100K meaningful connections strengthened
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValuesSection;